import { axios } from '/plugins/axios.client';
import { encodeSingleFieldToBase64, errorFromResponse } from '/src/common/general-utils';
import { i18n } from '/plugins/i18n.client';
const errorHandler = errorFromResponse(i18n.global.t.bind(i18n));
import { toast } from '/plugins/toaster.client.js';
import toParams from '/src/common/apis/to-params';

/**
 * Get a list of all action flows
 * required for displaying on the UI
 */
export const listActionFlows = async (params) => {
  try {
    return await axios.$get('/api/actionflows', {
      ...(params && { params: toParams(params, params.searchString) })
    });
  } catch (err) {
    toast.error(errorHandler(i18n.global.t('admin.action_flows.design.fetch_action_error'), err));
  }

  return { data: [] };
};

export const deleteActionFlow = async (UUID) => {
  try {
    await axios.$delete(`/api/actionflows/${UUID}`);
    toast.success(i18n.global.t('admin.action_flows.list.delete_success'));
  } catch (e) {
    toast.error(
      errorHandler(
        i18n.global.t('admin.dynamic_labels.delete_feature', {
          feature: i18n.global.t('admin.feature.action_flow', 2)
        }),
        e
      )
    );
  }

  return null;
};

export const getActionFlow = async (UUID) => {
  try {
    return await axios.$get(`/api/actionflows/${UUID}`);
  } catch (e) {
    toast.error(errorHandler(i18n.global.t('admin.action_flows.design.fetch_action_error'), e));
  }

  return { Settings: '' };
};

export const getContainerApps = async () => {
  try {
    return await axios.$get('/api/container-applications/list?status=deployed');
  } catch (e) {
    toast.error(errorHandler(i18n.global.t('admin.action_flows.design.fetch_container_app_error'), e));
  }
  return [];
};

/**
 * Creates an action flow
 * @param {Object} actionflowDetails Details of action flow that need to be saved
 */
export const createActionFlow = async (actionFlowDetails) => {
  try {
    if (actionFlowDetails.Settings.NotifyAtAllSteps) actionFlowDetails.Settings.Steps = [];

    const response = await axios.$post(`/api/actionflows`, {
      ...actionFlowDetails,
      Settings: JSON.stringify(actionFlowDetails.Settings)
    });
    toast.success(i18n.global.t('admin.action_flows.design.save_action'));
    return response;
  } catch (e) {
    toast.error(
      errorHandler(
        i18n.global.t('admin.dynamic_labels.creating_action', {
          feature: i18n.global.t('admin.feature.action_flow', 2)
        }),
        e
      )
    );
  }

  return null;
};

export const updateActionFlow = async ({ UUID, actionFlowDetails }) => {
  let payload = { ...actionFlowDetails };
  if (payload['Settings']) {
    payload.Settings = JSON.stringify(payload.Settings);
  }

  try {
    await axios.$put(`/api/actionflows/${UUID}`, {
      ...payload,
      Design: encodeSingleFieldToBase64(payload.Design)
    });
    toast.success(i18n.global.t('admin.action_flows.design.save_action'));
    return true;
  } catch (e) {
    toast.error(errorHandler(i18n.global.t('admin.action_flows.design.save_action_error'), e));
  }

  return null;
};

export const executeActionFlow = async ({ UUID, body }) => {
  try {
    return await axios.$post(`/api/actionflows/execute/${UUID}`, {
      ...body,
      design: encodeSingleFieldToBase64(body.design)
    });
  } catch (err) {
    toast.error(errorHandler(i18n.global.t('admin.action_flows.manage.error_execute'), err));
  }

  return {};
};

export const bulkTerminateExecutions = async (actionFlowUUID, executionUUIDs) => {
  try {
    const result = await axios.$post(`/api/actionflows/bulk/terminate/${actionFlowUUID}`, { executionUUIDs });
    if (result.length === 1) {
      toast[result[0].action](result[0].message);
    } else {
      toast['info'](i18n.global.t('admin.action_flows.history.bulk_termination_info'));
    }
  } catch (err) {
    toast.error(errorHandler(i18n.global.t('admin.action_flows.manage.error_terminate'), err));
  }
};

export const getExecutionSteps = async ({ UUID, xml }) => {
  try {
    return await axios.$post(`/api/actionflows/steps/${UUID}`, { xml: encodeSingleFieldToBase64(xml) });
  } catch (err) {
    toast.error(errorHandler(i18n.global.t('admin.action_flows.manage.get_data_api_array_error'), err));
  }

  return [];
};

export const getDetailBasedOnID = async (recipients, detailToFetch) => {
  try {
    return await axios.$get(
      `/api/users/getDetail?userIDs[]=${JSON.stringify(recipients)}&fieldToFetch=${detailToFetch}`
    );
  } catch (err) {
    toast.error(errorHandler(i18n.global.t('admin.action_flows.manage.get_user_detail_error'), err));
    return [];
  }
};

/**
 * Retrieves and updates api key of an action flow
 * @param {String} UUID Action flow's Unique identifier
 * @param {Boolean} updateKey Flag. true if action flow api key need to be updated
 */
export const getApiKey = async ({ UUID, updateKey }) => {
  try {
    return await axios.$get(`/api/actionflows/getApiKey/${UUID}?update=${updateKey}`);
  } catch (err) {
    toast.error(errorHandler(i18n.global.t('admin.action_flows.manage.fetch_api_key_error'), err));
  }

  return null;
};

/**
 * Rerieves list of jobs based on type
 * @param {String} type Job type that needs to be retrieved
 */
export const getJobs = async (type) => {
  try {
    const jobs = await axios.$get(`/api/job/listByType/${type}?joinJobHistory=false`);
    // updating ID field
    return jobs.map((job) => ({
      ...job,
      ID: job.jobId
    }));
  } catch (err) {
    toast.error(
      errorHandler(
        i18n.global.t('admin.dynamic_labels.fetch_action', {
          feature: i18n.global.t('admin.feature.job')
        }),
        err
      )
    );
  }

  return [];
};

/**
 * Retrieves an action flow's input variable based on UUID/xml
 * @param {String} UUID Action flow's Unique identifier
 * @param {String} diagram Action flow diagram's xml
 * @param {Boolean} getAllVariables Flag, if true returns all variables
 */
export const getInputVariableDetails = async ({ UUID, xml, getAllVariables }) => {
  try {
    return await axios.$post(`/api/actionflows/getInputVariableDetails/${UUID}`, {
      design: encodeSingleFieldToBase64(xml),
      getAllVariables
    });
  } catch (err) {
    toast.error(errorHandler(i18n.global.t('admin.action_flows.design.error_fetching_input_variable_details'), err));
  }

  return [];
};

/**
 * Retrieves execution history of an action flow
 * @param {String} UUID Action flow's Unique identifier
 * @param {object} params pagination details
 */
export const getExecutionHistory = async (UUID, params) => {
  try {
    return await axios.$get(`/api/actionflows/getHistoryLogs/${UUID}`, { params });
  } catch (err) {
    toast.error(errorHandler(i18n.global.t('admin.action_flows.design.error_fetching_execution_history'), err));
  }

  return { data: [] };
};

/**
 * Retrieves execution's detail
 * @param {String} executionUUID Execution's Unique identifier
 */
export const getExecutionEntry = async (executionUUID, fetchLatest, paginationParams = {}) => {
  try {
    return await axios.$get(`/api/actionflows/getExecutionEntry/${executionUUID}`, {
      params: {
        fetchLatest,
        limit: paginationParams.itemsPerPage,
        page: paginationParams.page,
        orderBy: paginationParams.sortBy?.[0]?.key,
        orderByDir: paginationParams.sortDesc?.[0] ? 'DESC' : 'ASC',
        search: paginationParams.search || null
      }
    });
  } catch (err) {
    toast.error(errorHandler(i18n.global.t('admin.action_flows.design.error_fetching_execution_entry'), err));
  }

  return [];
};

/**
 * Retrieves execution design xml
 * @param {String} ExecutionUUID Execution's Unique identifier
 */
export const getExecutionDesign = async (executionUUID) => {
  try {
    return await axios.$get(`/api/actionflows/getExecutionDesign/${executionUUID}`);
  } catch (err) {
    toast.error(errorHandler(i18n.global.t('admin.action_flows.design.error_fetching_execution_design'), err));
  }
  return null;
};

/**
 * Checks if execution design xml exists
 * @param {String} ExecutionUUID Execution's Unique identifier
 */
export const hasExecutionDesign = async (executionUUID) => {
  try {
    return await axios.$get(`/api/actionflows/hasExecutionDesign/${executionUUID}`);
  } catch (err) {
    return false;
  }
};

export const getWorkflowStates = async (workflowUUID) => {
  try {
    return await axios.$get(`/api/workflow/states/list/${workflowUUID}`);
  } catch (err) {
    toast.error(
      errorHandler(
        i18n.global.t('admin.dynamic_labels.fetch_action', {
          feature: i18n.global.t('admin.action_flows.design.workflow_states')
        }),
        err
      )
    );
  }
  return [];
};

export const getDefaultXml = () => `<?xml version="1.0" encoding="UTF-8"?>
<definitions xmlns="http://www.omg.org/spec/BPMN/20100524/MODEL"
 xmlns:bpmndi="http://www.omg.org/spec/BPMN/20100524/DI"
 xmlns:omgdc="http://www.omg.org/spec/DD/20100524/DC"
 xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
 xmlns:bpmnCustom="http://some-company/schema/bpmn/qa"
 xmlns:di="http://www.omg.org/spec/DD/20100524/DI"
 targetNamespace=""
 xsi:schemaLocation="http://www.omg.org/spec/BPMN/20100524/MODEL http://www.omg.org/spec/BPMN/2.0/20100501/BPMN20.xsd">
  <process id="Process_15u3inn">
    <extensionElements>
      <bpmnCustom:availableVariables />
    </extensionElements>
    <startEvent id="StartEvent_0o10lhk" name="Start" />
    <endEvent id="EndEvent_0o62v4w" name="End" />
  </process>
  <bpmndi:BPMNDiagram id="sid-74620812-92c4-44e5-949c-aa47393d3830">
    <bpmndi:BPMNPlane id="sid-cdcae759-2af7-4a6d-bd02-53f3352a731d" bpmnElement="Process_15u3inn">
      <bpmndi:BPMNShape id="StartEvent_0o10lhk_di" bpmnElement="StartEvent_0o10lhk">
        <omgdc:Bounds x="3" y="144" width="36" height="36" />
      </bpmndi:BPMNShape>
      <bpmndi:BPMNShape id="EndEvent_0o62v4w_di" bpmnElement="EndEvent_0o62v4w">
        <omgdc:Bounds x="219" y="144" width="36" height="36" />
      </bpmndi:BPMNShape>
    </bpmndi:BPMNPlane>
    <bpmndi:BPMNLabelStyle id="sid-e0502d32-f8d1-41cf-9c4a-cbb49fecf581">
      <omgdc:Font name="Arial" size="11" isBold="false" isItalic="false" isUnderline="false" isStrikeThrough="false" />
    </bpmndi:BPMNLabelStyle>
    <bpmndi:BPMNLabelStyle id="sid-84cb49fd-2f7c-44fb-8950-83c3fa153d3b">
      <omgdc:Font name="Arial" size="12" isBold="false" isItalic="false" isUnderline="false" isStrikeThrough="false" />
    </bpmndi:BPMNLabelStyle>
  </bpmndi:BPMNDiagram>
</definitions>`;

export const getHighlightClasses = (elm) => {
  if (elm.success) {
    return 'highlight';
  }
  return elm.processing || elm.success === null ? 'highlightProcessing' : 'highlightRed';
};

export const executeActionFlowWithVariableOnly = async (UUID, variables) =>
  await axios.$post(`/api/actionflows/execute-direct-with-name/${UUID}`, { inputVariables: variables });

export const getActionFlowUsageDetails = async (UUID) => {
  try {
    return await axios.$get(`/api/actionflows/getUsageDetails/${UUID}`);
  } catch (e) {
    toast.error(errorHandler(i18n.global.t('admin.action_flows.manage.fetch_usage_details_error'), e));
  }

  return [];
};

export const retriggerExecution = async (actionFlowUUID, executionUUID, design) => {
  try {
    await axios.$post(`/api/actionflows/retriggerActionFlow/${actionFlowUUID}/${executionUUID}`, {
      design: encodeSingleFieldToBase64(design)
    });
    toast.success(i18n.global.t('admin.action_flows.design.retrigger_action'));
  } catch (err) {
    toast.error(errorHandler(i18n.global.t('admin.action_flows.manage.error_execute'), err));
  }
  return {};
};
